import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
// eslint-disable-next-line import/no-unresolved
import {
  GoogleMap,
  InfoWindow,
  useJsApiLoader
} from '@react-google-maps/api';
import { useTheme } from '@mui/material/styles';
import {
  Card,
  Checkbox,
  FormControlLabel,
  Grid
} from '@mui/material';
import Loading from '../../../../components/loading';
import AdvancedMarker from '../../../../components/maps/AdvancedMarker';
import { getGoogleMapOptions, GOOGLE_MAPS_API_KEY } from '../../../../config/googleMaps';
import { useAuth } from '../../../../contexts/auth';
import subsidiaryService from '../../../../services/subsidiaryService';
import SupabaseService from '../../../../services/supabase';
import clientPointerImage from '../../../../assets/images/client-pointer.png';
import placePointerImage from '../../../../assets/images/placeholder.png';

interface ClientData {
  clientid: string;
  clientname: string;
  deliveries: number;
  environment: string;
  originlatitude: number;
  originlongitude: number;
  subsidiaryid: string;
  subsidiaryname: string;
  doc: any;
}

interface GoogleMapsPhoto {
  authorAttributions: any[];
  flagContentURI: string;
  googleMapsURI: string;
  heightPx: number;
  name: string;
  widthPx: number;
}

type CategoryFilter =
  | 'all'
  | 'restaurant'
  | 'meal_delivery'
  | 'meal_takeaway'
  | 'pharmacy'
  | 'pet_store'
  | 'store';

const CATEGORY_OPTIONS: Array<{ label: string; value: CategoryFilter }> = [
  { label: 'Todos', value: 'all' },
  { label: 'Restaurantes', value: 'restaurant' },
  { label: 'Delivery', value: 'meal_delivery' },
  { label: 'Para viagem', value: 'meal_takeaway' },
  { label: 'Farmácias', value: 'pharmacy' },
  { label: 'Pet Shops', value: 'pet_store' },
  { label: 'Varejo', value: 'store' }
];

const FALLBACK_CENTER = { lat: -3.71839, lng: -38.5434 }; // Fortaleza, Brasil (fallback)
const SEARCH_RADIUS_METERS = 7000; // 5km em metros (limite máximo da API)
const MAX_DISTANCE_KM = 80; // Limite máximo de distância em km

const GOOGLE_MAPS_LIBRARIES: ('places' | 'visualization' | 'marker')[] = ['places', 'visualization', 'marker'];

const mapContainerStyle = { width: '100%', height: '80vh' };

const ProspectingPage: React.FC = () => {
  const theme = useTheme();
  const { subsidiaryId, subsidiaryGuid } = useAuth();
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(FALLBACK_CENTER);
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');
  const [showCoverageArea, setShowCoverageArea] = useState<boolean>(false);
  const [showHeatmap, setShowHeatmap] = useState<boolean>(false);
  const [myClients, setMyClients] = useState<ClientData[]>([]);
  const [potentialClients, setPotentialClients] = useState<google.maps.places.PlaceResult[]>([]);
  const [activePlaceId, setActivePlaceId] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const heatmapLayerRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);
  const lastFetchRequestRef = useRef<number>(0);
  const supabaseService = new SupabaseService();

  // Carrega a subsidiária e define o centro do mapa
  useEffect(() => {
    const loadSubsidiary = async () => {
      const subsidiaryIdToUse = subsidiaryGuid || subsidiaryId;
      if (subsidiaryIdToUse) {
        try {
          const subsidiary = await subsidiaryService.getByIdAsync(subsidiaryIdToUse);
          if (subsidiary?.address?.location) {
            setMapCenter({
              lat: subsidiary.address.location.latitude,
              lng: subsidiary.address.location.longitude
            });
          }
        } catch (error) {
          console.error('Erro ao carregar subsidiária:', error);
          // Mantém o fallback center em caso de erro
        }
      }
    };

    loadSubsidiary();
  }, [subsidiaryId, subsidiaryGuid]);

  /**
   * Carrega o SDK do Google Maps junto com Places + Visualization libraries.
   * Visualization não é usada diretamente agora, mas fica disponível para futuras
   * camadas (heatmaps, densidade etc.), conforme pedido no briefing.
   * 'places' agora é carregado na versão beta para acesso a nova API searchNearby.
   */
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'prospecting-map-sdk',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: 'beta'
  });

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);
    /**
     * PlacesService precisa do mapa inicializado. Instanciar aqui evita recriações
     * e garante que os filtros usem a mesma referência.
     */
    placesServiceRef.current = new google.maps.places.PlacesService(map);
  }, []);

  const handleMapUnmount = useCallback(() => {
    mapRef.current = null;
    setMapReady(false);
    placesServiceRef.current = null;
    if (heatmapLayerRef.current) {
      heatmapLayerRef.current.setMap(null);
      heatmapLayerRef.current = null;
    }
  }, []);

  const handleCategoryChange: React.ChangeEventHandler<HTMLSelectElement> = (event) => {
    setSelectedCategory(event.target.value as CategoryFilter);
  };

  const typesToQuery = useMemo(() => {
    if (selectedCategory === 'all') {
      return CATEGORY_OPTIONS.filter((opt) => opt.value !== 'all').map((opt) => opt.value);
    }
    return [selectedCategory];
  }, [selectedCategory]);

  const getCallsComplete15Days = useCallback(async () => {
    if (!subsidiaryId) return;
    try {
      const clients = await supabaseService.getCallsComplete15Days(subsidiaryId) as ClientData[];
      const normalizedClients = (clients || [])
        .map((client) => {
          const lat = typeof client.originlatitude === 'string'
            ? parseFloat(client.originlatitude)
            : client.originlatitude;
          const lng = typeof client.originlongitude === 'string'
            ? parseFloat(client.originlongitude)
            : client.originlongitude;

          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return null;
          }

          return {
            ...client,
            originlatitude: lat,
            originlongitude: lng
          };
        })
        .filter((client): client is ClientData => client !== null);

      setMyClients(normalizedClients);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setMyClients([]);
    }
  }, [subsidiaryId]);

  const handleCheckCoverageArea = useCallback((checked: boolean) => {
    setShowCoverageArea(checked);
  }, []);

  const handleCheckHeatmap = useCallback((checked: boolean) => {
    setShowHeatmap(checked);
  }, []);

  // Carrega ou limpa dados baseado nos checkboxes
  useEffect(() => {
    if (showCoverageArea || showHeatmap) {
      // Se pelo menos um checkbox está marcado e não há dados, carrega
      if (myClients.length === 0) {
        getCallsComplete15Days();
      }
    } else {
      // Se ambos estão desmarcados, limpa os dados
      setMyClients([]);
    }
  }, [showCoverageArea, showHeatmap, getCallsComplete15Days, myClients.length]);

  // Calcula a distância em km entre dois pontos usando a fórmula de Haversine
  const calculateDistance = useCallback((lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Raio da Terra em km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLng = (lng2 - lng1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180))
      * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  const runPlacesSearch = useCallback(async () => {
    if (!mapRef.current || !placesServiceRef.current || typeof google === 'undefined') {
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    const requestId = Date.now();
    lastFetchRequestRef.current = requestId;

    const aggregatedResults: Map<string, google.maps.places.PlaceResult> = new Map();

    const fetchByType = async (type: CategoryFilter) => {
      if (!mapRef.current) return;

      // Mapeamento dos tipos antigos para os novos tipos da Places API (New)
      // https://developers.google.com/maps/documentation/places/web-service/place-types
      const typeMapping: Record<string, string[]> = {
        restaurant: ['restaurant'],
        meal_delivery: ['meal_delivery'],
        meal_takeaway: ['meal_takeaway'],
        pharmacy: ['pharmacy'],
        pet_store: ['pet_store'],
        store: ['store', 'shopping_mall', 'supermarket']
      };

      const includedTypes = typeMapping[type] || [type];

      // @ts-ignore - A tipagem da nova API ainda não está completa no @types/google.maps
      const { Place, SearchNearbyRankPreference } = await google.maps.importLibrary('places') as any;

      const center = mapRef.current.getCenter();
      if (!center) return;

      const request = {
        fields: ['location', 'displayName', 'id', 'photos', 'types', 'priceLevel', 'rating', 'userRatingCount', 'businessStatus', 'formattedAddress', 'internationalPhoneNumber', 'adrFormatAddress'],
        locationRestriction: {
          center: { lat: center.lat(), lng: center.lng() },
          radius: SEARCH_RADIUS_METERS
        },
        includedTypes,
        maxResultCount: 20,
        rankPreference: SearchNearbyRankPreference.POPULARITY,
        language: 'pt-BR'
      };

      console.log(`[Prospecting] Buscando type=${type} com includedTypes=`, includedTypes);

      try {
        const { places } = await Place.searchNearby(request);

        console.log(`[Prospecting] Resultados para ${type}:`, places?.length ?? 0);

        if (places && places.length > 0) {
          places.forEach((place: any) => {
            // Adaptando a resposta da nova API para o formato esperado pelo componente
            // ou usando as propriedades diretamente se refatorarmos o render
            const adaptedPlace: any = {
              place_id: place.Dg?.id,
              geometry: {
                location: {
                  lat: () => place.Dg?.location.lat,
                  lng: () => place.Dg?.location.lng
                }
              },
              name: place.Dg?.displayName,
              photos: place.Dg?.photos?.map((p: GoogleMapsPhoto) => ({
                height: p.heightPx,
                width: p.widthPx,
                getUrl: (opts: any) => {
                  const maxWidth = opts?.maxWidth || p.widthPx;
                  const maxHeight = opts?.maxHeight || p.heightPx;
                  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                  return `https://places.googleapis.com/v1/${p.name}/media?key=${apiKey}&maxHeightPx=${maxHeight}&maxWidthPx=${maxWidth}`;
                }
              })),
              types: place.Dg?.types,
              price_level: (() => {
                if (place.Dg?.priceLevel === 'PRICE_LEVEL_INEXPENSIVE') return 1;
                if (place.Dg?.priceLevel === 'PRICE_LEVEL_MODERATE') return 2;
                if (place.Dg?.priceLevel === 'PRICE_LEVEL_EXPENSIVE') return 3;
                if (place.Dg?.priceLevel === 'PRICE_LEVEL_VERY_EXPENSIVE') return 4;
                return null;
              })(),
              business_status: place.Dg?.businessStatus,
              rating: place.Dg?.rating,
              user_ratings_total: place.Dg?.userRatingCount,
              formatted_phone_number: place.Dg?.internationalPhoneNumber, // Nova API prefere internationalPhoneNumber
              vicinity: place.Dg?.formattedAddress // A nova API retorna address components, formattedAddress é o mais próximo de vicinity simples
            };

            // Verificação de abertura precisa ser feita separadamente na nova API se não vier no searchNearby
            // Mas businessStatus já ajuda. isOpen() requer place details ou fields específicos.
            // Para simplificar, assumimos OPERATIONAL como aberto ou usamos a propriedade isOpen se disponível no futuro.
            adaptedPlace.opening_hours = {
              isOpen: () => place.Dg?.businessStatus === 'OPERATIONAL'
            };

            const placeLat = place.Dg?.location.lat;
            const placeLng = place.Dg?.location.lng;

            const distance = calculateDistance(
              mapCenter.lat,
              mapCenter.lng,
              placeLat,
              placeLng
            );

            if (distance <= MAX_DISTANCE_KM) {
              aggregatedResults.set(place.Dg?.id, adaptedPlace);
            }
          });
        }
      } catch (error) {
        console.error(`Erro ao buscar locais do tipo ${type}:`, error);
        setSearchError('Erro ao buscar locais com a nova API.');
      }
    };

    await Promise.all(typesToQuery.map((type) => fetchByType(type)));

    if (lastFetchRequestRef.current === requestId) {
      setPotentialClients(Array.from(aggregatedResults.values()));
    }

    setIsSearching(false);
  }, [typesToQuery, mapCenter, calculateDistance]);

  useEffect(() => {
    runPlacesSearch();
  }, [selectedCategory, runPlacesSearch]);

  /**
   * O callback onIdle dispara quando o usuário termina de interagir (arrastar/zoom).
   * Usamos esse gatilho para sincronizar a busca do Places com a área visível do mapa.
   */
  const handleMapIdle = useCallback(() => {
    runPlacesSearch();
  }, [runPlacesSearch]);

  /**
   * Cria e atualiza a camada de heatmap para exibir a densidade dos clientes
   * sempre que myClients ou showHeatmap for atualizado.
   */
  useEffect(() => {
    // Cleanup function que sempre será retornada
    const cleanup = () => {
      if (heatmapLayerRef.current) {
        heatmapLayerRef.current.setMap(null);
        heatmapLayerRef.current = null;
      }
    };

    if (!mapRef.current || !isLoaded || typeof google === 'undefined' || !google.maps.visualization) {
      return cleanup;
    }

    // Remove a camada anterior se existir
    if (heatmapLayerRef.current) {
      heatmapLayerRef.current.setMap(null);
      heatmapLayerRef.current = null;
    }

    // Se o heatmap não está ativado ou não há clientes, não cria a camada
    if (!showHeatmap || myClients.length === 0) {
      return cleanup;
    }

    // Prepara os dados para o heatmap
    const heatmapData = myClients.map((client) => ({
      location: new google.maps.LatLng(client.originlatitude, client.originlongitude),
      weight: client.deliveries || 1 // Usa o número de entregas como peso
    }));

    // Cria a nova camada de heatmap
    const heatmap = new google.maps.visualization.HeatmapLayer({
      data: heatmapData,
      map: mapRef.current,
      radius: 30,
      opacity: 0.6,
      gradient: [
        'rgba(0, 255, 255, 0)',
        'rgba(0, 255, 255, 1)',
        'rgba(0, 191, 255, 1)',
        'rgba(0, 127, 255, 1)',
        'rgba(0, 63, 255, 1)',
        'rgba(0, 0, 255, 1)',
        'rgba(0, 0, 223, 1)',
        'rgba(0, 0, 191, 1)',
        'rgba(0, 0, 159, 1)',
        'rgba(0, 0, 127, 1)',
        'rgba(63, 0, 91, 1)',
        'rgba(127, 0, 63, 1)',
        'rgba(191, 0, 31, 1)',
        'rgba(255, 0, 0, 1)'
      ]
    });

    heatmapLayerRef.current = heatmap;

    return cleanup;
  }, [myClients, isLoaded, showHeatmap]);

  if (loadError) {
    return (
      <p style={{ fontFamily: theme.typography.fontFamily, color: theme.palette.text.primary }}>
        Não foi possível carregar o Google Maps:
        <br />
        <span>{loadError.message}</span>
      </p>
    );
  }

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} sm={12} md={12}>
        <Card className="card-box mb-4">
          <div className="card-header py-3">
            <div className="card-header--title font-size-lg">Prospecção de Clientes (Beta)</div>

          </div>
          <div className="w-100" style={{ width: '100%', height: '80vh' }}>
            {isLoaded ? (
              <div style={{ position: 'relative', width: '100%' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    zIndex: 1000,
                    backgroundColor: theme.palette.background.paper,
                    padding: '0.75rem',
                    borderRadius: '8px',
                    boxShadow: theme.shadows[2],
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}
                  >
                    <label
                      htmlFor="category-filter"
                      style={{
                        fontWeight: theme.typography.fontWeightMedium,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.35rem',
                        color: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: theme.typography.body1.fontSize
                      }}
                    >
                      Categoria
                      <select
                        id="category-filter"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        style={{ minWidth: '220px', padding: '0.5rem' }}
                      >
                        {CATEGORY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div
                        style={{
                          fontSize: theme.typography.body2.fontSize,
                          color: theme.palette.text.secondary,
                          fontFamily: theme.typography.fontFamily,
                          marginTop: '0.25rem'
                        }}
                      >
                        {isSearching ? 'Buscando locais...' : `Exibindo ${potentialClients.length} clientes em potencial`}
                        {searchError ? (
                          <span
                            style={{
                              color: theme.palette.error.main,
                              fontSize: theme.typography.body2.fontSize,
                              display: 'block',
                              marginTop: '0.5rem',
                              fontFamily: theme.typography.fontFamily
                            }}
                          >
                            {searchError}
                            <br />
                            Tente reposicionar o mapa ou altere a categoria.
                          </span>
                        ) : null}
                      </div>
                    </label>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '4px',
                        backgroundColor: theme.palette.background.default
                      }}
                    >
                      <div
                        style={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.body2.fontSize,
                          fontWeight: theme.typography.fontWeightMedium,
                          color: theme.palette.text.primary,
                          marginBottom: '0.25rem'
                        }}
                      >
                        Área de Atuação
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '1rem',
                          flexWrap: 'wrap'
                        }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={showCoverageArea}
                              onChange={(e) => handleCheckCoverageArea(e.target.checked)}
                              size="small"
                            />
                          )}
                          label="Exibir pointer"
                          style={{
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.body2.fontSize,
                            color: theme.palette.text.primary
                          }}
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={showHeatmap}
                              onChange={(e) => handleCheckHeatmap(e.target.checked)}
                              size="small"
                            />
                          )}
                          label="Exibir mapa de calor"
                          style={{
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.body2.fontSize,
                            color: theme.palette.text.primary
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <GoogleMap
                  onLoad={handleMapLoad}
                  onUnmount={handleMapUnmount}
                  onIdle={handleMapIdle}
                  center={mapCenter}
                  zoom={14}
                  mapContainerStyle={mapContainerStyle}
                  options={getGoogleMapOptions({
                    streetViewControl: false,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    zoomControl: true,
                    zoomControlOptions: {
                      position: google.maps.ControlPosition.TOP_RIGHT
                    }
                  })}
                >
                  {mapReady && mapRef.current && potentialClients.map((place) => {
                    const placeId = place.place_id;
                    if (!place.geometry?.location || !placeId) {
                      return null;
                    }

                    const position = {
                      lat: place.geometry.location.lat(),
                      lng: place.geometry.location.lng()
                    };

                    const photoUrl = place.photos?.[0]?.getUrl({ maxWidth: 300, maxHeight: 160 });
                    const primaryType = place.types?.[0];
                    const priceLevel = typeof place.price_level === 'number' ? '$'.repeat(place.price_level + 1) : 'N/D';
                    const isOpen = place.opening_hours?.isOpen?.() ?? place.business_status === 'OPERATIONAL';
                    // const reservationUrl = `https://www.google.com/maps/search/?api=1&query_place_id=${placeId}`;
                    const ratingDisplay = typeof place.rating === 'number'
                      ? `${place.rating.toFixed(1)} ★ (${place.user_ratings_total?.toLocaleString('pt-BR') ?? 'N/D'})`
                      : 'Sem avaliações';

                    // const details = placeDetails.get(placeId);
                    const phoneNumber = place?.formatted_phone_number || place?.international_phone_number;

                    return (
                      <React.Fragment key={placeId}>
                        <AdvancedMarker
                          map={mapRef.current}
                          position={position}
                          onClick={() => {
                            setActivePlaceId(placeId);
                          }}
                          icon={{
                            url: clientPointerImage,
                            scaledSize: new google.maps.Size(32, 41),
                            anchor: new google.maps.Point(16, 41)
                          }}
                        />
                        {activePlaceId === placeId ? (
                          <InfoWindow position={position} onCloseClick={() => setActivePlaceId(null)}>
                            <div
                              style={{
                                width: '260px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: theme.shadows[4],
                                fontFamily: theme.typography.fontFamily,
                                color: theme.palette.text.primary
                              }}
                            >
                              {photoUrl ? (
                                <div
                                  style={{
                                    height: '140px',
                                    backgroundImage: `url(${photoUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                  }}
                                />
                              ) : null}

                              <div style={{ padding: '0.9rem', backgroundColor: theme.palette.background.paper }}>
                                <h3
                                  style={{
                                    margin: 0,
                                    fontSize: theme.typography.h6.fontSize,
                                    fontWeight: theme.typography.fontWeightMedium,
                                    fontFamily: theme.typography.fontFamily,
                                    color: theme.palette.text.primary
                                  }}
                                >
                                  {place.name}
                                </h3>

                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                    margin: '0.35rem 0',
                                    color: theme.palette.warning.main,
                                    fontSize: theme.typography.body2.fontSize,
                                    fontWeight: theme.typography.fontWeightMedium,
                                    fontFamily: theme.typography.fontFamily
                                  }}
                                >
                                  <span>{ratingDisplay}</span>
                                </div>

                                <p
                                  style={{
                                    margin: '0.25rem 0',
                                    color: theme.palette.text.secondary,
                                    fontSize: theme.typography.body2.fontSize,
                                    fontFamily: theme.typography.fontFamily
                                  }}
                                >
                                  {primaryType?.replace(/_/g, ' ') ?? 'Categoria não informada'}
                                  {' '}
                                  •
                                  {' '}
                                  {priceLevel}
                                </p>

                                <p
                                  style={{
                                    margin: '0.25rem 0',
                                    color: isOpen ? theme.palette.success.main : theme.palette.error.main,
                                    fontWeight: theme.typography.fontWeightMedium,
                                    fontFamily: theme.typography.fontFamily
                                  }}
                                >
                                  {isOpen ? 'Aberto agora' : 'Fechado'}
                                </p>

                                {place.vicinity ? (
                                  <p
                                    style={{
                                      margin: '0.25rem 0',
                                      color: theme.palette.text.secondary,
                                      fontSize: theme.typography.caption.fontSize,
                                      fontFamily: theme.typography.fontFamily
                                    }}
                                  >
                                    {place.vicinity}
                                  </p>
                                ) : null}

                                {phoneNumber ? (
                                  <p
                                    style={{
                                      margin: '0.25rem 0',
                                      color: theme.palette.text.secondary,
                                      fontSize: theme.typography.caption.fontSize,
                                      fontFamily: theme.typography.fontFamily
                                    }}
                                  >
                                    📞
                                    {' '}
                                    <a
                                      href={`tel:${phoneNumber.replace(/\s/g, '')}`}
                                      style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                                    >
                                      {phoneNumber}
                                    </a>
                                  </p>
                                ) : null}
                              </div>

                              {/* <div style={{ padding: '0.75rem', backgroundColor: theme.palette.grey[100], textAlign: 'center' }}>
                        <a
                          href={reservationUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'inline-flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            padding: '0.55rem 1rem',
                            borderRadius: '999px',
                              backgroundColor: theme.palette.success.main,
                              color: theme.palette.common.white,
                              fontWeight: theme.typography.fontWeightMedium,
                            textDecoration: 'none',
                              fontSize: theme.typography.body1.fontSize,
                              fontFamily: theme.typography.fontFamily
                          }}
                        >
                          Reservar uma mesa
                        </a>
                        </div> */}
                            </div>
                          </InfoWindow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}

                  {/* Marcadores dos clientes existentes (quando checkbox está ativado) */}
                  {mapReady && mapRef.current && showCoverageArea && myClients.map((client) => {
                    const clientMarkerId = `client-${client.clientid}`;
                    const clientPosition = {
                      lat: client.originlatitude,
                      lng: client.originlongitude
                    };

                    return (
                      <React.Fragment key={clientMarkerId}>
                        <AdvancedMarker
                          map={mapRef.current}
                          position={clientPosition}
                          onClick={() => {
                            setActivePlaceId(clientMarkerId);
                          }}
                          icon={{
                            url: placePointerImage,
                            scaledSize: new google.maps.Size(44, 44),
                            anchor: new google.maps.Point(14, 43)
                          }}
                        />
                        {activePlaceId === clientMarkerId ? (
                          <InfoWindow position={clientPosition} onCloseClick={() => setActivePlaceId(null)}>
                            <div
                              style={{
                                width: '260px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: theme.shadows[4],
                                fontFamily: theme.typography.fontFamily,
                                color: theme.palette.text.primary
                              }}
                            >
                              <div style={{ padding: '0.9rem', backgroundColor: theme.palette.background.paper }}>
                                <h3
                                  style={{
                                    margin: 0,
                                    fontSize: theme.typography.h6.fontSize,
                                    fontWeight: theme.typography.fontWeightMedium,
                                    fontFamily: theme.typography.fontFamily,
                                    color: theme.palette.text.primary
                                  }}
                                >
                                  {client.clientname}
                                </h3>
                                <p
                                  style={{
                                    margin: '0.25rem 0',
                                    color: theme.palette.text.secondary,
                                    fontSize: theme.typography.body2.fontSize,
                                    fontFamily: theme.typography.fontFamily
                                  }}
                                >
                                  Entregas:
                                  {' '}
                                  {client.deliveries}
                                </p>

                                <p
                                  style={{
                                    margin: '0.25rem 0',
                                    color: theme.palette.text.secondary,
                                    fontSize: theme.typography.body2.fontSize,
                                    fontFamily: theme.typography.fontFamily
                                  }}
                                >
                                  Franquia:
                                  {' '}
                                  {client.subsidiaryname}
                                </p>
                              </div>
                            </div>
                          </InfoWindow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </GoogleMap>
                {isSearching && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 2000,
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Loading height="100%" />
                  </div>
                )}
              </div>
            ) : (
              <p style={{ fontFamily: theme.typography.fontFamily, color: theme.palette.text.primary }}>Carregando mapa...</p>
            )}

            <footer
              style={{
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.secondary,
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: `1px solid ${theme.palette.divider}`
              }}
            >
              <p
                style={{
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.text.secondary,
                  margin: 0,
                  fontSize: '0.875rem'
                }}
              >
                Visualize potenciais parceiros (restaurantes, farmácias, pet shops e varejo) utilizando os dados do Google Places.
              </p>
            </footer>

            {/* <footer style={{ fontSize: '0.85rem', color: '#4a5568' }}>
        {
          Como iniciar as APIs:
          - Maps: forneça NEXT_PUBLIC_GOOGLE_MAPS_API_KEY no .env.local e deixe o useJsApiLoader importar o script.
          - Places: carregada via 'places' na prop libraries; instanciada em handleMapLoad usando google.maps.places.PlacesService.
          - Visualization: já está disponível pela mesma lista de libs; basta criar camadas (ex: HeatmapLayer) e controlá-las nos filtros.
          Filtros: a mudança do <select> altera selectedCategory, dispara novas buscas (runPlacesSearch) e atualiza os marcadores.
        }
      </footer> */}
          </div>
        </Card>
      </Grid>
    </Grid>

  );
};

export default ProspectingPage;
